import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators/property.js';

/**
 * @event {CustomEvent} quantitychange - Fired when the quantity changes. Detail contains the new quantity.
 * @event {CustomEvent} remove - Fired when the quantity is decremented below the minimum.
 */
export class QuantityInputComponent extends LitElement {
  @property({ type: Number, attribute: 'min' })
  min = 1;

  @property({ type: Number, attribute: 'max' })
  max = Number.MAX_SAFE_INTEGER;

  @property({ type: Number, attribute: 'step' })
  step = 1;

  @property({ type: Number, attribute: 'quantity' })
  quantity = 1;

  protected override render() {
    return html`
      <button @click="${this.handleDecrement}">&minus;</button>
      <input type="number" value="${this.quantity}" min="${this.min}" max="${this.max}" step="${this.step}" @input="${this.handleInput}" />
      <button @click="${this.handleIncrement}">&plus;</button>
    `;
  }

  private handleDecrement() {
    const newQuantity = this.quantity - this.step;
    if (newQuantity >= this.min) {
      this.quantity = newQuantity;
      this.dispatchEvent(new CustomEvent('quantitychange', { detail: { quantity: this.quantity } }));
    } else {
      this.dispatchEvent(new CustomEvent('remove'));
    }
  }

  private handleIncrement() {
    const newQuantity = this.quantity + this.step;
    if (newQuantity <= this.max) {
      this.quantity = newQuantity;
      this.dispatchEvent(new CustomEvent('quantitychange', { detail: { quantity: this.quantity } }));
    } else {
      this.quantity = this.max;
    }
  }

  private handleInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let newQuantity = Number.parseInt(input.value, 10);
    if (Number.isNaN(newQuantity)) {
      newQuantity = this.min;
    }
    if (newQuantity < this.min) {
      this.dispatchEvent(new CustomEvent('remove'));
    } else if (newQuantity > this.max) {
      this.quantity = this.max;
      this.dispatchEvent(new CustomEvent('quantitychange', { detail: { quantity: this.quantity } }));
    } else {
      this.quantity = newQuantity;
      this.dispatchEvent(new CustomEvent('quantitychange', { detail: { quantity: this.quantity } }));
    }
  }

  static override get styles() {
    return [
      css`
        :host {
          display: flex;
          gap: 1px;
        }

        input {
          background: var(--accent-1);
          appearance: textfield;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          width: 100%;
          max-width: 8ch;
          padding: 0.5rem;
          border-inline: 1px solid var(--background);
          border-radius: 0;
          font-variant-numeric: tabular-nums;
        }

        button {
          --button-radius: 0.33rem;
          border: none;
          background: var(--accent-1);
          display: grid;
          place-content: center;
          padding: 0.5rem 0.75rem;
          cursor: pointer;

          &:hover {
            background: color-mix(in srgb, var(--accent-1) 80%, transparent);
          }

          &:first-child {
            border-top-left-radius: var(--button-radius);
            border-bottom-left-radius: var(--button-radius);
          }

          &:last-child {
            border-top-right-radius: var(--button-radius);
            border-bottom-right-radius: var(--button-radius);
          }
        }
      `,
    ];
  }
}

export function defineQuantityInputComponent(): void {
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInputComponent);
  }
}
